import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `news.articles.el-gobierno-te-paga-el-frigo-nuevo.${f}`

const REF = {
    mintur: {
        href: 'https://www.mintur.gob.es/es-es/GabinetePrensa/NotasPrensa/2026/Paginas/El-Gobierno-pone-en-marcha-plan-de-choque-de-15-M%E2%82%AC-para-renovar-equipamiento-de-hosteler%C3%ADa-.aspx',
        domain: 'mintur.gob.es',
        label: 'Mintur',
    },
    proyectoRd: {
        href: 'https://turismo.gob.es/es-es/participacion-publica/paginas/detalleparticipacionpublica.aspx?k=769',
        domain: 'turismo.gob.es',
        label: 'Proyecto de Real Decreto',
    },
    boeRd: {
        href: 'https://www.boe.es/diario_boe/txt.php?id=BOE-A-2026-16554',
        domain: 'boe.es',
        label: 'RD 638/2026 · BOE',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
    cae: {
        href: 'https://caes.es/precio-cae-2026/',
        domain: 'caes.es',
        label: 'CAE España',
    },
    rdCae: {
        href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2023-2027',
        domain: 'boe.es',
        label: 'RD 36/2023 · BOE',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

function TipiLink({ children }: { children?: ReactNode }) {
    const { t } = useTranslation()
    return (
        <LocaleLink to='/' className={article.link}>
            {children ?? t(k('tipiLink'), 'Tipi')}
        </LocaleLink>
    )
}

export function PlanChoqueFrigoArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo del plan de choque para renovar equipamiento y me gustaría que me ayudéis a ver si encaja con mi local.',
        ),
    )

    const weekActions: ReactNode[] = [
        t(
            k('weekActions.1'),
            'Identifica qué equipo te cuesta más. No el más viejo: el que más consume. Suele ser el frío, no la cocina.',
        ),
        t(k('weekActions.2'), 'Pide dos presupuestos ya, con etiqueta energética e instalación desglosada.'),
        t(k('weekActions.3'), 'Busca técnico competente para la memoria de ahorro. Esto es lo que tarda.'),
        t(
            k('weekActions.4'),
            'Ten a mano el certificado censal de Hacienda y el de minimis (si has cobrado otras ayudas de minimis en tres años, ojo con el tope).',
        ),
        <>
            <strong>{t(k('weekActions.5.strong'), 'Mira tu caja de aquí a diciembre.')}</strong>
        </>,
        <>
            {t(k('weekActions.6.before'), 'El ')}
            <strong>{t(k('weekActions.6.deadline'), '5 de agosto a las 10:00')}</strong>
            {t(k('weekActions.6.after'), ', presentar. No a las 12.')}
        </>,
    ]

    const sources = [
        {
            label: t(k('sources.1'), 'Nota de prensa del Ministerio de Industria y Turismo (28/07/2026)'),
            href: REF.mintur.href,
        },
        {
            label: t(
                k('sources.rd638'),
                'Real Decreto 638/2026, plan de choque para renovar equipamiento en hostelería, BOE',
            ),
            href: REF.boeRd.href,
        },
        {
            label: t(k('sources.2'), 'Real Decreto 36/2023, sistema de Certificados de Ahorro Energético, BOE'),
            href: REF.rdCae.href,
        },
        {
            label: t(k('sources.3'), 'Orden TED/133/2026, obligaciones de ahorro energético para 2026, BOE'),
            href: 'https://www.boe.es/diario_boe/txt.php?id=BOE-A-2026-4522',
        },
        { label: t(k('sources.4'), 'Precio del CAE en 2026, CAE España'), href: REF.cae.href },
        { label: t(k('sources.5'), 'Anuario 2025, Hostelería de España'), href: REF.anuario.href },
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(
                                k('dek.before11000'),
                                'Plan de choque de 15 M€ para renovar equipamiento en hostelería. Hasta ',
                            )}
                            <strong>11.000€</strong>
                            {t(k('dek.after11000Before100'), ' por local, el ')}
                            <strong>100%</strong>
                            {t(k('dek.after100BeforeOpen'), ' de la inversión, por orden de llegada. Se abre el ')}
                            <strong>{t(k('dek.opensAt'), '5 de agosto de 2026 a las 10:00')}</strong>
                            {t(k('dek.afterOpenBeforeClose'), ' y cierra el ')}
                            <strong>{t(k('dek.closesAt'), '30 de septiembre a las 15:00')}</strong>
                            {t(k('dek.afterClose'), ', o antes si se acaba el dinero.')}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Chef concentrado en una cocina profesional con horno y estación de acero inoxidable',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <div className={article.proseIntro}>
                    <p className={article.p}>
                        {t(
                            k('intro.1'),
                            '¿Quién no se ha aguantado haciendo un arreglo de apaño con ese frigorífico que no cambias porque te costó un ojo de la cara?',
                        )}
                    </p>
                    <p className={article.p}>
                        {t(
                            k('intro.2.before'),
                            'Ese que hace un ruido raro desde hace dos veranos y que sigue ahí porque cambiarlo son 4.000€ que ahora mismo no están. Pues esta vez parece que hay suerte: el Consejo de Ministros ',
                        )}
                        <span className={article.cite}>
                            {t(k('intro.2.cite'), 'aprobó el 28 de julio un Real Decreto')}
                            <SourceChip source='mintur' />
                        </span>{' '}
                        {t(
                            k('intro.2.after'),
                            'que pone 15 millones sobre la mesa para que renovemos la maquinaria vieja. Y no es un préstamo. Es a fondo perdido.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('intro.3'),
                            'Ahora bien, solo la van a pillar los más ágiles. Porque el que no corre, vuela. Y te lo digo con números.',
                        )}
                    </p>
                </div>

                <section id='en-cristiano'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.enCristiano'), 'Cuánto, quién, cuándo y cómo se reparte')}
                    </h2>
                    <div className={article.tableWrap}>
                        <table className={article.tableFacts}>
                            <tbody>
                                <tr>
                                    <th scope='row'>{t(k('table.howMuch.label'), 'Cuánto')}</th>
                                    <td>
                                        {t(k('table.howMuch.before'), 'Hasta ')}
                                        <strong>11.000€</strong>
                                        {t(
                                            k('table.howMuch.after'),
                                            ' por establecimiento, hasta el 100% de la inversión',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.who.label'), 'Quién')}</th>
                                    <td>
                                        {t(
                                            k('table.who.value'),
                                            'Bares, restaurantes, hoteles, campings y catering (CNAE 55.10 a 55.90 y 56.10 a 56.30), de alta en el Censo de Empresarios',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.forWhat.label'), 'Para qué')}</th>
                                    <td>
                                        {t(
                                            k('table.forWhat.value'),
                                            'Hornos, planchas, freidoras, cámaras y armarios frigoríficos, mesas refrigeradas, lavavajillas, climatización, toldos. También herramientas digitales que midan el consumo',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.opens.label'), 'Se abre')}</th>
                                    <td>
                                        <span className={article.cite}>
                                            <strong>{t(k('table.opensAt'), '5 de agosto de 2026, 10:00h')}</strong>
                                            <SourceChip source='mintur' />
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.closes.label'), 'Se cierra')}</th>
                                    <td>
                                        {t(k('table.closes.before'), '30 de septiembre, 15:00h, ')}
                                        <strong>{t(k('table.closes.strong'), 'o cuando se acabe el dinero')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.allocation.label'), 'Cómo se reparte')}</th>
                                    <td>
                                        {t(
                                            k('table.allocation.value'),
                                            'Por orden de entrada. El primero que llega, el primero que cobra',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.howMany.label'), 'Cuántas')}</th>
                                    <td>
                                        {t(
                                            k('table.howMany.value'),
                                            'Una por establecimiento. Tres locales, tres solicitudes',
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('warning.before'), 'Un aviso: ')}
                            <strong className={article.cite}>
                                {t(
                                    k('warning.strong'),
                                    'queda excluido cambiar un equipo por otro de combustible fósil',
                                )}
                                <SourceChip source='mintur' />
                            </strong>
                            {t(
                                k('warning.after'),
                                ', gas natural incluido. Si ibas a renovar la cocina de gas por otra de gas, esto no te sirve.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='detalle-que-casi-nadie-lee'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.detalle'), 'Elegible desde marzo: las dos fechas que te dejan fuera')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <strong className={article.cite}>
                                {t(k('detalle.p1.strong'), 'El gasto es elegible desde el 1 de marzo de 2026.')}
                                <SourceChip source='boeRd' />
                            </strong>
                        </p>
                        <p className={article.p}>
                            {t(
                                k('detalle.p2.before'),
                                'O sea: si en marzo se te murió la cámara y la cambiaste a toda prisa, esa factura puede entrar. Solo necesitas que la factura y el justificante de pago caigan entre el ',
                            )}
                            <strong>{t(k('detalle.p2.dates'), '1 de marzo y el 20 de octubre de 2026')}</strong>
                            {t(k('detalle.p2.after'), '.')}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(k('detalle.datesIntro'), 'Dos fechas que se llevan a la gente por delante:')}
                        </p>
                        <ul className={article.dashListGap2xl}>
                            <li className={article.dashItem}>
                                <strong>{t(k('detalle.date1.label'), '20 de octubre')}</strong>
                                {t(
                                    k('detalle.date1.after'),
                                    ': todo ejecutado y pagado. No vale con haberlo encargado.',
                                )}
                            </li>
                            <li className={article.dashItem}>
                                <strong>{t(k('detalle.date2.label'), '31 de octubre')}</strong>
                                {t(k('detalle.date2.after'), ': presentada la justificación.')}
                            </li>
                        </ul>
                    </div>
                </section>

                <aside className={article.breakout} aria-label={t(k('breakout.aria'), 'Cambio de marcha')}>
                    <div className={article.breakCopy}>
                        <p className={article.breakEyebrow}>{t(k('breakout.eyebrow'), 'Cambio de marcha')}</p>
                        <p className={article.breakLead}>
                            {t(
                                k('breakout.lead'),
                                'Hasta aquí la buena noticia. Ahora viene lo que pillará a más de uno con la guardia baja.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('breakout.p.before'),
                                'Porque renovar la cocina con dinero público suena genial… hasta que descubres que la subvención llega ',
                            )}
                            <em>{t(k('breakout.p.em'), 'después')}</em>
                            {t(k('breakout.p.after'), ' de haber pagado tú el equipo.')}
                        </p>
                    </div>
                </aside>

                <section id='el-dinero-lo-pones-tu'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.dineroTu'), 'Antes de la parte bonita: el dinero lo pones tú')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <strong className={article.cite}>
                                {t(k('dinero.p1.strong'), 'Esta ayuda se cobra después.')}
                                <SourceChip source='proyectoRd' />
                            </strong>{' '}
                            {t(
                                k('dinero.p1.after'),
                                'Compras el equipo, lo instalas, lo pagas, lo justificas. Y entonces te ingresan la subvención de una vez.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(k('dinero.p2.before'), 'Con un calendario tan apretado, lo más probable es que ')}
                            <strong>
                                {t(
                                    k('dinero.p2.strong'),
                                    'tengas que pagar el equipo sin saber todavía si te la han concedido',
                                )}
                            </strong>
                            {t(k('dinero.p2.after'), '.')}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('dinero.p3'),
                                'Traducido: necesitas tener 9.000 u 11.000€ disponibles varios meses. Si tu caja no aguanta eso, esta ayuda no es para ti aunque cumplas todos los requisitos. Mejor saberlo hoy que en octubre.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='cuenta-de-la-vieja'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.cuentaVieja'), '15 millones dan para el 0,5% del sector')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('cuenta.p1.before'), '15 millones, a un máximo de 11.000€ por local, dan para ')}
                            <strong>1.363 establecimientos</strong>
                            {t(k('cuenta.p1.after'), '. Si la media baja a 5.000€, dan para 3.000.')}
                        </p>
                        <p className={article.p}>
                            {t(k('cuenta.p2.before'), 'En España hay ')}
                            <span className={article.cite}>
                                <strong>
                                    {t(k('cuenta.p2.strong'), 'más de 300.000 establecimientos de hostelería')}
                                </strong>
                                <SourceChip source='anuario' />
                            </span>
                            .
                        </p>
                        <p className={article.p}>
                            {t(k('cuenta.p3.before'), 'Haz la división: esto alcanza a entre el ')}
                            <strong>0,5% y el 1% del sector</strong>
                            {t(
                                k('cuenta.p3.mid'),
                                '. Y como se reparte por orden de entrada, no gana el que más lo necesita, sino el que tiene los papeles listos el ',
                            )}
                            <strong>{t(k('cuenta.p3.deadline'), '5 de agosto a las 10:00')}</strong>
                            {t(k('cuenta.p3.after'), '.')}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('cuenta.p4.before'), 'El cuello de botella no es el formulario. Es la ')}
                            <strong>
                                {t(
                                    k('cuenta.p4.strong'),
                                    'memoria de ahorro energético firmada por técnico competente',
                                )}
                            </strong>
                            {t(
                                k('cuenta.p4.after'),
                                '. Eso no se improvisa. Si empiezas a buscar perito el día que abre el plazo, llegas tarde.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='cae'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>{t(k('h2.cae'), 'Y ahora lo de los CAE')}</h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('cae.p1.before'), 'Desde ')}
                            <TipiLink />{' '}
                            {t(
                                k('cae.p1.afterBeforeStrong'),
                                'sabemos que esto no acaba en la subvención, porque existe algo que mucho hostelero ni sabe que existe: los ',
                            )}
                            <strong>{t(k('cae.p1.strong'), 'CAE, Certificados de Ahorro Energético')}</strong>
                            {t(k('cae.p1.afterStrong'), '.')}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('cae.p2.before'),
                                'Funciona así: cambias un equipo viejo por uno eficiente, ese ahorro se certifica, y el certificado se lo compran las energéticas, que están obligadas por ley. Cada kWh que dejas de consumir al año vale hoy ',
                            )}
                            <span className={article.cite}>
                                {t(k('cae.p2.citeBefore'), 'entre ')}
                                <strong>0,09 y 0,10€</strong>
                                <SourceChip source='cae' />
                            </span>
                            .
                        </p>
                        <p className={article.p}>
                            {t(k('cae.p3'), 'Suena a 2x1. Pero aquí viene la letra pequeña, y prefiero contártela yo:')}
                        </p>
                        <p className={article.p}>
                            <strong className={article.cite}>
                                {t(
                                    k('cae.p4.strong'),
                                    'El proyecto de Real Decreto dice que estas ayudas «no serán compatibles con otras subvenciones, ayudas, ingresos o recursos vigentes para los mismos costes subvencionables».',
                                )}
                                <SourceChip source='proyectoRd' />
                            </strong>
                        </p>
                        <p className={article.p}>
                            {t(
                                k('cae.p5.before'),
                                'Traducido: si la ayuda te cubre el 100% de la factura, cobrar además el CAE por ',
                            )}
                            <em>{t(k('cae.p5.em'), 'esa misma factura')}</em>
                            {t(k('cae.p5.mid'), ' es terreno resbaladizo. Y la normativa de CAE te pedirá ')}
                            <span className={article.cite}>
                                {t(k('cae.p5.cite'), 'justificar el «efecto incentivador»')}
                                <SourceChip source='rdCae' />
                            </span>{' '}
                            {t(
                                k('cae.p5.after'),
                                'del certificado, difícil de argumentar cuando no has puesto un euro.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            <strong>
                                {t(k('cae.p6.strong'), '¿Dónde sí juega el CAE, entonces? En tres sitios:')}
                            </strong>
                        </p>
                        <ol className={article.numListGap2xl}>
                            <li className={article.numItem}>
                                <strong>{t(k('cae.list.1.strong'), 'En lo que pagas tú.')}</strong>
                                {t(
                                    k('cae.list.1.after'),
                                    ' Si tu renovación son 18.000€ y la ayuda tapa 11.000€, los otros 7.000€ son inversión tuya y ahí el CAE no compite con nada.',
                                )}
                            </li>
                            <li className={article.numItem}>
                                <strong>{t(k('cae.list.2.strong'), 'En lo que la ayuda no cubre.')}</strong>
                                {t(
                                    k('cae.list.2.after'),
                                    ' Iluminación LED, aerotermia, aislamiento de cámara, sustitución de compresores. Nada de eso entra en este plan y todo eso genera CAE.',
                                )}
                            </li>
                            <li className={article.numItem}>
                                <strong>{t(k('cae.list.3.strong'), 'Como plan B si llegas tarde.')}</strong>
                                {t(
                                    k('cae.list.3.after'),
                                    ' Que es lo que le va a pasar a la mayoría. Si se agota el crédito, el CAE sigue ahí sobre la factura entera.',
                                )}
                            </li>
                        </ol>
                    </div>
                </section>

                <section id='ejemplo-numeros'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.ejemploNumeros'), 'Un ejemplo con números')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('ejemplo.intro'),
                                'Armario frigorífico de bar, quince años, de los que arrancan cada dos por tres:',
                            )}
                        </p>
                        <ul className={article.dashList}>
                            <li className={article.dashItem}>
                                <strong>{t(k('ejemplo.list.1.label'), 'Consume hoy:')}</strong>
                                {t(k('ejemplo.list.1.value'), ' unos 3.600 kWh al año')}
                            </li>
                            <li className={article.dashItem}>
                                <strong>{t(k('ejemplo.list.2.label'), 'Uno nuevo eficiente:')}</strong>
                                {t(k('ejemplo.list.2.value'), ' unos 1.400 kWh')}
                            </li>
                            <li className={article.dashItem}>
                                <strong>{t(k('ejemplo.list.3.label'), 'Ahorro:')}</strong>
                                {t(k('ejemplo.list.3.value'), ' 2.200 kWh al año')}
                            </li>
                        </ul>
                        <p className={cn(article.pNote, article.pGapMd)}>
                            {t(
                                k('ejemplo.note'),
                                'Estimación propia a partir de consumos típicos de armario frigorífico comercial.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(k('ejemplo.calcsLabel'), 'Las tres cuentas:')}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('ejemplo.bill.label'), 'Factura de la luz.')}</strong>
                            {t(
                                k('ejemplo.bill.text'),
                                ' 2.200 kWh × 0,22 €/kWh = 480€ menos cada año. Este es el dinero de verdad, porque se repite todos los años.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('ejemplo.grant.label'), 'La ayuda.')}</strong>
                            {t(
                                k('ejemplo.grant.text'),
                                ' Armario, instalación y gestión del residuo: unos 4.500€. Si sumas lavavajillas y memoria técnica te plantas en 9.500€, dentro del tope. Subvención: hasta esos 9.500€.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('ejemplo.cae.label'), 'El CAE.')}</strong>
                            {t(
                                k('ejemplo.cae.text'),
                                ' 2.200 kWh × 0,095€ = 210€, pago único. Pero recuerda: si la ayuda cubrió el 100%, este no lo cobras por esa factura.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('ejemplo.outro.before'),
                                'Así que el titular no es «te dan el frigo gratis y encima te pagan». Es: ',
                            )}
                            <strong>
                                {t(
                                    k('ejemplo.outro.strong'),
                                    'te pueden dar el frigo gratis y a partir de ahí te ahorras 480€ al año',
                                )}
                            </strong>
                            {t(k('ejemplo.outro.after'), ' que antes se te iban sin que los vieras en ningún sitio.')}
                        </p>
                    </div>
                </section>

                <section id='esta-semana'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Qué preparar antes del 5 de agosto')}
                    </h2>
                    <div className={article.prose}>
                        <NewsArticleNumList items={weekActions} />
                    </div>
                </section>

                <section id='pendientes'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.pendientes'), '5.000 € en la nota, 2.000 € en el proyecto: qué manda')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('pendientes.p1.before'), 'La nota del Ministerio habla de ayudas ')}
                            <span className={article.cite}>
                                {t(k('pendientes.p1.citeBefore'), '«de entre ')}
                                <strong>5.000 y 11.000 euros</strong>
                                {t(k('pendientes.p1.citeAfter'), '»')}
                                <SourceChip source='mintur' />
                            </span>
                            .{' '}
                            {t(
                                k('pendientes.p1.mid'),
                                ' El proyecto de Real Decreto que ha circulado entre despachos fija una ',
                            )}
                            <strong className={article.cite}>
                                {t(k('pendientes.p1.strong'), 'inversión mínima de 2.000€')}
                                <SourceChip source='proyectoRd' />
                            </strong>
                            .{' '}
                            {t(
                                k('pendientes.p1.after'),
                                'No es lo mismo, y hasta que no salga el texto en el BOE no se sabe si con 3.000€ de inversión te quedas fuera.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('pendientes.p2.before'),
                                'Igual con el listado exacto de equipos y los requisitos de eficiencia mínima. Todo esto sale de la nota oficial y del proyecto de decreto: ',
                            )}
                            <strong>{t(k('pendientes.p2.strong'), 'el texto que manda es el del BOE')}</strong>
                            {t(k('pendientes.p2.after'), '.')}
                        </p>
                    </div>
                </section>

                <div className={article.prose}>
                    <p className={article.p}>
                        {t(
                            k('closing.p1'),
                            'Si crees que en tu local hay una máquina que te está sangrando la factura sin que te des cuenta, dínoslo y le echamos un ojo a la factura.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(k('closing.p2'), 'El que no corre, vuela: y el BOE manda sobre la nota.')}
                    </p>
                </div>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Crees que esta ayuda encaja con tu local?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Cuéntanos qué equipo quieres renovar y te ayudamos a ver si tiene sentido entrar o si te conviene más el CAE.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                        <li>
                            {t(
                                k('sources.extra'),
                                'Guías sobre el proyecto de Real Decreto publicadas por CISLE, Hostelería Madrid e Innóvate 4.0',
                            )}
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
